/**
 * Tracking & Attribution Module
 *
 * Captures and persists UTM parameters and advertising platform identifiers
 * throughout the user journey for attribution tracking.
 *
 * Captures:
 * - UTM parameters (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 * - Meta (Facebook) identifiers (fbclid, _fbp, _fbc)
 * - TikTok identifiers (ttclid, _ttp)
 *
 * Storage: localStorage with 30-day expiry
 *
 * Usage:
 * 1. Call captureTrackingParams() on every page load (App.tsx)
 * 2. Call getTrackingData() when needed (checkout, forms, etc.)
 */

import { sanitizeInput } from '@shared/utils';

const TRACKING_PARAMS = [
  // UTM parameters
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',

  // Meta (Facebook) parameters
  'fbclid',

  // TikTok parameters
  'ttclid',
] as const;

const TRACKING_COOKIES = [
  '_fbp',  // Facebook Browser ID
  '_fbc',  // Facebook Click cookie
  '_ttp',  // TikTok Pixel cookie
] as const;

const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

interface StoredValue {
  value: string;
  expiry: number; // timestamp
}

export interface TrackingData {
  // UTM parameters
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;

  // Meta identifiers
  fbclid: string | null;
  fbp: string | null;
  fbc: string | null;

  // TikTok identifiers
  ttclid: string | null;
  ttp: string | null;
}

/**
 * Read a cookie by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
}

/**
 * Store value in localStorage with expiry and sanitization
 */
function setWithExpiry(key: string, value: string): void {
  // Sanitize input before storing
  const sanitizedValue = sanitizeInput(value);

  const now = Date.now();
  const item: StoredValue = {
    value: sanitizedValue,
    expiry: now + EXPIRY_MS,
  };

  try {
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    // Only log in development to avoid exposing data
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Tracking] Failed to store ${key}:`, error);
    }
  }
}

/**
 * Get value from localStorage, checking expiry
 */
function getWithExpiry(key: string): string | null {
  try {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;

    const item: StoredValue = JSON.parse(itemStr);
    const now = Date.now();

    // Check if expired
    if (now > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Tracking] Failed to read ${key}:`, error);
    }
    return null;
  }
}

/**
 * Capture tracking parameters from URL and cookies
 * Call this on every page load to ensure parameters are captured
 */
export function captureTrackingParams(): void {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    let capturedCount = 0;

    // Capture URL parameters
    TRACKING_PARAMS.forEach((param) => {
      const value = params.get(param);
      if (value) {
        setWithExpiry(param, value);
        capturedCount++;
        // Only log in development, don't expose values
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Tracking] Captured ${param}`);
        }
      }
    });

    // Capture platform cookies
    TRACKING_COOKIES.forEach((cookieName) => {
      const value = getCookie(cookieName);
      if (value) {
        setWithExpiry(cookieName, value);
        capturedCount++;
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Tracking] Captured ${cookieName}`);
        }
      }
    });

    if (capturedCount > 0 && process.env.NODE_ENV === 'development') {
      console.log(`[Tracking] Captured ${capturedCount} tracking identifier(s)`);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Tracking] Error capturing parameters:', error);
    }
  }
}

/**
 * Get all tracking data from storage
 * Returns null for any missing values
 */
export function getTrackingData(): TrackingData {
  const data: TrackingData = {
    utm_source: getWithExpiry('utm_source'),
    utm_medium: getWithExpiry('utm_medium'),
    utm_campaign: getWithExpiry('utm_campaign'),
    utm_content: getWithExpiry('utm_content'),
    utm_term: getWithExpiry('utm_term'),
    fbclid: getWithExpiry('fbclid'),
    fbp: getWithExpiry('_fbp') || getCookie('_fbp'), // Fallback to current cookie
    fbc: getWithExpiry('_fbc') || getCookie('_fbc'),
    ttclid: getWithExpiry('ttclid'),
    ttp: getWithExpiry('_ttp') || getCookie('_ttp'),
  };

  return data;
}

/**
 * Check if any tracking data exists
 */
export function hasTrackingData(): boolean {
  const data = getTrackingData();
  return Object.values(data).some(value => value !== null);
}

/**
 * Clear all tracking data (for testing or privacy)
 */
export function clearTrackingData(): void {
  TRACKING_PARAMS.forEach(param => {
    localStorage.removeItem(param);
  });

  TRACKING_COOKIES.forEach(cookieName => {
    localStorage.removeItem(cookieName);
  });

  if (process.env.NODE_ENV === 'development') {
    console.log('[Tracking] All tracking data cleared');
  }
}

/**
 * Debug: Log all current tracking data (development only)
 */
export function debugTrackingData(): void {
  if (process.env.NODE_ENV === 'development') {
    const data = getTrackingData();
    console.log('[Tracking] Current tracking data:', data);

    const hasData = hasTrackingData();
    console.log('[Tracking] Has tracking data:', hasData);
  }
}
